import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { locationsAPI, organizationsAPI, locationHealthAPI } from '../lib/api';
import { sortByName } from '../lib/sortByName';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    MapPin,
    Search,
    Plus,
    Building2,
    MoreVertical,
    Edit2,
    Trash2,
    HelpCircle,
    ChevronDown,
    ChevronUp,
    Star,
    Users,
    Calendar,
    CheckCircle,
    AlertTriangle,
    Loader2,
    Zap,
    Info,
    FileSpreadsheet,
    Copy,
    Check,
    ExternalLink,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { serializeLocationSettings } from '../utils/serializeLocationSettings';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Label } from '../components/ui/label';
import QuickSetBaselinesModal from '../components/QuickSetBaselinesModal';
import CSVBaselineImport from '../components/CSVBaselineImport';
import LocationRoutingSection from '../components/LocationRoutingSection';
import { 
    EmptyState, 
    InfoBanner,
    ViewOnlyBanner,
    FeatureIntro 
} from '../components/UsabilityComponents';
import { stringifyDetail } from '../lib/utils';
import { parseUtc } from '../lib/datetime';

// Help tooltip component for user-friendly design
const HelpTooltip = ({ text }) => (
    <TooltipProvider>
        <Tooltip>
            <TooltipTrigger asChild>
                <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help inline-block ml-1" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs z-[100]">
                <p className="text-sm">{text}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

// Platform icons/colors for visual clarity
const PLATFORM_CONFIG = {
    google: { 
        name: 'Google', 
        color: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700',
        iconBg: 'bg-fuchsia-100',
        description: 'Check your Google Business Profile'
    },
    facebook: { 
        name: 'Facebook', 
        color: 'bg-indigo-50 border-indigo-200 text-indigo-700',
        iconBg: 'bg-indigo-100',
        description: 'Check your Facebook Business Page'
    },
    instagram: { 
        name: 'Instagram', 
        color: 'bg-pink-50 border-pink-200 text-pink-700',
        iconBg: 'bg-pink-100',
        description: 'Check your Instagram profile'
    },
    yelp: { 
        name: 'Yelp', 
        color: 'bg-red-50 border-red-200 text-red-700',
        iconBg: 'bg-red-100',
        description: 'Check your Yelp Business page'
    },
    trustpilot: { 
        name: 'Trustpilot', 
        color: 'bg-green-50 border-green-200 text-green-700',
        iconBg: 'bg-green-100',
        description: 'Check your Trustpilot profile'
    }
};

const Locations = () => {
    const { t } = useTranslation();
    const [locations, setLocations] = useState([]);
    // Map of { [location_id]: { card_id, id, status } } — derived from backend.
    const [neoMasterCards, setNeoMasterCards] = useState({});
    // Provision dialog state
    const [neoDialog, setNeoDialog] = useState({ open: false, locationId: null, locationName: '', code: '', submitting: false });
    // Success panel state (shown after create, with copyable URL)
    const [neoCreated, setNeoCreated] = useState({ open: false, cardId: '', url: '', copied: false });
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    // Per-location auto-clear for open recovery alerts (0 = Off). Saved on
    // pick, server-enforced: a location manager may only set its own location.
    const [autoClearDays, setAutoClearDays] = useState(0);
    const saveAutoClear = async (days) => {
        if (!editingLocation?.id) return;
        setAutoClearDays(days);
        try {
            await locationsAPI.setAutoClearRecovery(editingLocation.id, days);
            setLocations((prev) => prev.map((l) => l.id === editingLocation.id
                ? { ...l, auto_clear_recovery_days: days } : l));
            toast.success(t('locations.autoClear.saved'));
        } catch {
            toast.error(t('locations.autoClear.failed'));
        }
    };
    // Single-source-of-truth dirty detection (see /app/frontend/src/utils/serializeLocationSettings.js).
    // Snapshot is captured at modal-open time; `isLocationDirty` recomputes whenever
    // form state changes and compares against this baseline.
    const [locationSettingsSnapshot, setLocationSettingsSnapshot] = useState(null);
    const [showBaseline, setShowBaseline] = useState(false);
    const [saving, setSaving] = useState(false);
    const [quickSetOpen, setQuickSetOpen] = useState(false);
    const [csvImportOpen, setCsvImportOpen] = useState(false);
    // P_PHASE_4_8 — manual "current snapshot" editor state. Independent
    // of formData so the Save button can fire its own dedicated PATCH
    // without touching baseline.
    const [currentSnapshot, setCurrentSnapshot] = useState({
        google_reviews: '',
        google_rating: '',
        snapshot_updated_at: null,
    });
    const [savingSnapshot, setSavingSnapshot] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        organization_id: '',
        baseline_values: {
            google_reviews: '',
            google_rating: '',
            facebook_likes: '',
            instagram_followers: '',
            yelp_reviews: '',
            yelp_rating: '',
            trustpilot_reviews: '',
            trustpilot_rating: '',
            baseline_date: new Date().toISOString().split('T')[0]
        }
    });
    
    const { user, canManageLocations, isSuperAdmin, isPartner, isClientAdmin, isViewOnly, canEditBaselines } = useAuth();

    // Deterministic dirty-state detection.
    // Both sides go through `serializeLocationSettings`, which returns a JSON
    // string. Compare the strings directly — no double-stringify, no object
    // identity traps. Mirrors the proven Cards.jsx implementation.
    const isLocationDirty = useMemo(() => {
        if (!locationSettingsSnapshot) return false;
        const current = serializeLocationSettings(formData, 'state');
        return current !== locationSettingsSnapshot;
    }, [formData, locationSettingsSnapshot]);

    // Badge shows only when editing an existing location.
    const isEditingLocation = Boolean(editingLocation?.id);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [locationsRes] = await Promise.all([
                locationsAPI.list()
            ]);
            setLocations(sortByName(locationsRes.data));
            
            // Only super admin can see all organizations
            if (isSuperAdmin()) {
                const orgsRes = await organizationsAPI.list();
                setOrganizations(sortByName(orgsRes.data));
            } else if (user?.organization_id) {
                const orgRes = await organizationsAPI.get(user.organization_id);
                setOrganizations([orgRes.data]);
            }

            // Fetch which locations already have a Master Manager NEO card.
            // Non-fatal: if it fails the page still renders, we just show the
            // Create button until next load.
            try {
                const API = window.location.origin;
                const token = localStorage.getItem('yulontap_token');
                const cardsRes = await fetch(`${API}/api/neo/master-cards`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (cardsRes.ok) {
                    const data = await cardsRes.json();
                    setNeoMasterCards(data.cards || {});
                }
            } catch { /* silent */ }
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (location = null) => {
        if (location) {
            setEditingLocation(location);
            setAutoClearDays(location.auto_clear_recovery_days ?? 0);
            const baseline = location.baseline_values || {};
            const cv = location.current_values || {};
            setCurrentSnapshot({
                google_reviews: cv.google_reviews ?? '',
                google_rating: cv.google_rating ?? '',
                snapshot_updated_at: cv.snapshot_updated_at || null,
            });
            setFormData({
                name: location.name,
                address: location.address || '',
                organization_id: location.organization_id,
                baseline_values: {
                    google_reviews: baseline.google_reviews ?? '',
                    google_rating: baseline.google_rating ?? '',
                    facebook_likes: baseline.facebook_likes ?? '',
                    instagram_followers: baseline.instagram_followers ?? '',
                    yelp_reviews: baseline.yelp_reviews ?? '',
                    yelp_rating: baseline.yelp_rating ?? '',
                    trustpilot_reviews: baseline.trustpilot_reviews ?? '',
                    trustpilot_rating: baseline.trustpilot_rating ?? '',
                    baseline_date: baseline.baseline_date || new Date().toISOString().split('T')[0]
                }
            });
            // Auto-expand baseline section if baseline exists
            setShowBaseline(!!location.baseline_values);
        } else {
            setEditingLocation(null);
            setAutoClearDays(0);
            setCurrentSnapshot({
                google_reviews: '',
                google_rating: '',
                snapshot_updated_at: null,
            });
            setFormData({
                name: '',
                address: '',
                organization_id: user?.organization_id || organizations[0]?.id || '',
                baseline_values: {
                    google_reviews: '',
                    google_rating: '',
                    facebook_likes: '',
                    instagram_followers: '',
                    yelp_reviews: '',
                    yelp_rating: '',
                    trustpilot_reviews: '',
                    trustpilot_rating: '',
                    baseline_date: new Date().toISOString().split('T')[0]
                }
            });
            setShowBaseline(false);
        }
        // Capture the snapshot AFTER formData is queued so the dirty memo
        // compares against the same logical state the form was opened with.
        // We use the serializer with whichever side we have:
        //   - editing existing → location object straight from API
        //   - creating new     → empty/default form-state shape
        if (location) {
            setLocationSettingsSnapshot(serializeLocationSettings(location, 'location'));
        } else {
            // Build a clean state-shaped snapshot from the freshly-set defaults.
            setLocationSettingsSnapshot(serializeLocationSettings({
                name: '',
                address: '',
                organization_id: user?.organization_id || organizations[0]?.id || '',
                baseline_values: {
                    google_reviews: '', google_rating: '',
                    facebook_likes: '', instagram_followers: '',
                    yelp_reviews: '', yelp_rating: '',
                    trustpilot_reviews: '', trustpilot_rating: '',
                    baseline_date: new Date().toISOString().split('T')[0],
                },
            }, 'state'));
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            toast.error('Please enter location name');
            return;
        }
        if (!formData.organization_id) {
            toast.error('Please select an organization');
            return;
        }

        setSaving(true);
        
        // Build baseline values (only include non-empty values)
        const baselineValues = {};
        let hasBaseline = false;
        
        Object.entries(formData.baseline_values).forEach(([key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                if (key === 'baseline_date') {
                    baselineValues[key] = value;
                } else if (key.includes('rating')) {
                    const numVal = parseFloat(value);
                    if (!isNaN(numVal) && numVal >= 0 && numVal <= 5) {
                        baselineValues[key] = numVal;
                        hasBaseline = true;
                    }
                } else {
                    const numVal = parseInt(value);
                    if (!isNaN(numVal) && numVal >= 0) {
                        baselineValues[key] = numVal;
                        hasBaseline = true;
                    }
                }
            }
        });

        const payload = {
            name: formData.name.trim(),
            address: formData.address.trim() || null,
            organization_id: formData.organization_id,
            baseline_values: hasBaseline ? baselineValues : null
        };

        try {
            if (editingLocation) {
                await locationsAPI.update(editingLocation.id, payload);
                toast.success('Location updated successfully');
            } else {
                await locationsAPI.create(payload);
                toast.success('Location created successfully');
            }
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Operation failed');
        } finally {
            setSaving(false);
        }
    };

    // P_PHASE_4_8_REPUTATION_BASELINE_FALLBACK — save the manually-
    // maintained "today" reputation snapshot. Independent of the main
    // Save button so baseline values stay untouched.
    const handleSaveCurrentSnapshot = async () => {
        if (!editingLocation?.id) return;
        const reviews = currentSnapshot.google_reviews === '' ? null : parseInt(currentSnapshot.google_reviews, 10);
        const rating = currentSnapshot.google_rating === '' ? null : parseFloat(currentSnapshot.google_rating);
        if (reviews !== null && (Number.isNaN(reviews) || reviews < 0)) {
            toast.error('Reviews must be a non-negative number');
            return;
        }
        if (rating !== null && (Number.isNaN(rating) || rating < 1 || rating > 5)) {
            toast.error('Rating must be between 1.0 and 5.0');
            return;
        }
        setSavingSnapshot(true);
        try {
            const res = await locationsAPI.updateCurrentSnapshot(editingLocation.id, {
                google_reviews: reviews,
                google_rating: rating,
            });
            const cv = res.data?.current_values || {};
            setCurrentSnapshot({
                google_reviews: cv.google_reviews ?? '',
                google_rating: cv.google_rating ?? '',
                snapshot_updated_at: cv.snapshot_updated_at || null,
            });
            toast.success('Current snapshot saved');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save snapshot');
        } finally {
            setSavingSnapshot(false);
        }
    };

    const handleDelete = async (locationId) => {
        if (!window.confirm('Are you sure you want to delete this location?')) {
            return;
        }

        try {
            await locationsAPI.delete(locationId);
            toast.success('Location deleted');
            loadData();
        } catch (error) {
            toast.error('Failed to delete location');
        }
    };

    const getOrgName = (orgId) => {
        return organizations.find(o => o.id === orgId)?.name || 'Unknown';
    };

    const hasBaselineData = (location) => {
        const b = location.baseline_values;
        if (!b) return false;
        return b.google_reviews != null || b.facebook_likes != null || 
               b.instagram_followers != null || b.yelp_reviews != null || 
               b.trustpilot_reviews != null;
    };

    const filteredLocations = locations.filter(loc => 
        loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.address?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const updateBaselineField = (field, value) => {
        setFormData(prev => ({
            ...prev,
            baseline_values: {
                ...prev.baseline_values,
                [field]: value
            }
        }));
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="skeleton h-20 rounded-xl" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="skeleton h-40 rounded-xl" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="locations-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('locations.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {t('locations.subtitle')}
                    </p>
                </div>
                {!isViewOnly() && (
                    <div className="flex items-center gap-2">
                        {canEditBaselines() && (
                            <>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button 
                                                variant="outline"
                                                onClick={() => setCsvImportOpen(true)}
                                                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                                data-testid="csv-import-btn"
                                                data-insight-id="locations.workflow.csv-import"
                                                data-insight-label="CSV baseline import"
                                            >
                                                <FileSpreadsheet className="w-4 h-4 mr-2" />
                                                {t('locations.csvImport')}
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                            <p className="text-sm">Upload a CSV file to set baseline values for hundreds of locations at once. Perfect for large clients.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button 
                                                variant="outline"
                                                onClick={() => setQuickSetOpen(true)}
                                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                                data-testid="quick-set-baselines-btn"
                                                data-insight-id="locations.workflow.quick-set-baselines"
                                                data-insight-label="Quick Set Baselines"
                                            >
                                                <Zap className="w-4 h-4 mr-2" />
                                                {t('locations.quickSet')}
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                            <p className="text-sm">Quickly enter starting values for multiple locations at once. Essential for accurate ROI tracking.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </>
                        )}
                        {canManageLocations() && !isPartner() && (
                            <Button 
                                onClick={() => openModal()}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid="add-location-btn"
                                data-insight-id="locations.action.add"
                                data-insight-label="Add Location"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {t('locations.addLocation')}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* View Only Banner for Franchise Admin */}
            {isViewOnly() && (
                <ViewOnlyBanner 
                    role="Franchise Admin"
                    message="You have view-only access to location data. Contact an admin to make changes."
                />
            )}

            {/* Baseline Explanation for new users */}
            {!isViewOnly() && filteredLocations.length > 0 && filteredLocations.filter(l => !hasBaselineData(l)).length > 3 && (
                <InfoBanner type="tip" title={t('locations.setBaselineTitle')}>
                    {t('locations.setBaselineDesc').split('<bold>')[0]}
                    <strong>{t('locations.newReviews')}</strong>
                    {t('locations.setBaselineDesc').split('</bold>')[1]}
                </InfoBanner>
            )}

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder={t('locations.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="search-locations-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Locations Grid */}
            {filteredLocations.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredLocations.map((location, idx) => (
                        <Card 
                            key={location.id} 
                            className="card-hover"
                            data-testid={`location-${location.id}`}
                            data-insight-id="locations.card"
                            data-insight-label="Location card"
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                                            <MapPin className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-foreground">{location.name}</p>
                                            {isSuperAdmin() && (
                                                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                                                    <Building2 className="w-3 h-3" />
                                                    {getOrgName(location.organization_id)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {canManageLocations() && !isViewOnly() && !isPartner() && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                                                    data-insight-id={idx === 0 ? "locations.workflow.actions-menu" : undefined}
                                                    data-insight-label={idx === 0 ? "Location actions menu" : undefined}
                                                >
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => openModal(location)}
                                                    data-insight-id={idx === 0 ? "locations.workflow.edit" : undefined}
                                                    data-insight-label={idx === 0 ? "Edit location" : undefined}
                                                    data-testid={`location-edit-${location.id}`}
                                                >
                                                    <Edit2 className="w-4 h-4 mr-2" />
                                                    Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem 
                                                    onClick={() => handleDelete(location.id)}
                                                    className="text-red-600"
                                                    data-insight-id={idx === 0 ? "locations.workflow.delete" : undefined}
                                                    data-insight-label={idx === 0 ? "Delete location" : undefined}
                                                    data-testid={`location-delete-${location.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </div>

                                {location.address && (
                                    <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm text-muted-foreground mb-3">
                                        {location.address}
                                    </div>
                                )}

                                {/* Baseline Status Badge */}
                                <div className="flex items-center gap-2 mb-3">
                                    {hasBaselineData(location) ? (
                                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                            {t('common.baselineSet')}
                                        </Badge>
                                    ) : (
                                        <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                            {t('common.noBaseline')}
                                        </Badge>
                                    )}
                                    {location.baseline_values?.baseline_date && (
                                        <span className="text-xs text-muted-foreground">
                                            {t('common.since')} {location.baseline_values.baseline_date}
                                        </span>
                                    )}
                                </div>

                                {/* Tap Health Monitoring Toggle — Super Admin only, opt-in (default OFF) */}
                                {isSuperAdmin() && (
                                    <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-3" data-testid={`tap-health-block-${location.id}`}>
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">{t('tapHealth.monitoringTitle')}</p>
                                            <p className="text-xs text-muted-foreground">{t('tapHealth.monitoringDesc')}</p>
                                        </div>
                                        <button
                                            type="button"
                                            data-testid={`tap-health-toggle-${location.id}`}
                                            onClick={async () => {
                                                const next = !location.tap_health_monitoring_enabled;
                                                try {
                                                    const API = window.location.origin;
                                                    const token = localStorage.getItem('yulontap_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
                                                    const res = await fetch(`${API}/api/admin/tap-health/locations/${location.id}/monitoring`, {
                                                        method: 'PUT',
                                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                                        body: JSON.stringify({ enabled: next }),
                                                    });
                                                    if (!res.ok) throw new Error('toggle failed');
                                                    location.tap_health_monitoring_enabled = next;
                                                    toast.success(next ? t('tapHealth.enabledToast') : t('tapHealth.disabledToast'));
                                                    loadData();
                                                } catch (e) {
                                                    toast.error(t('tapHealth.toggleFailed'));
                                                }
                                            }}
                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${location.tap_health_monitoring_enabled ? 'bg-amber-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                                            aria-pressed={!!location.tap_health_monitoring_enabled}
                                            aria-label="Toggle Tap Health Monitoring"
                                        >
                                            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${location.tap_health_monitoring_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                                        </button>
                                    </div>
                                )}

                                {/* NEO Feature Toggle — Super Admin only */}
                                {isSuperAdmin() && (
                                    <div className="space-y-2 mb-3">
                                    <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-gradient-to-r from-fuchsia-500/10 to-purple-500/10 border border-fuchsia-500/30">
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-fuchsia-700 dark:text-fuchsia-300">NEO</p>
                                            <p className="text-xs text-muted-foreground truncate">{t('common.managerControlCenter')}</p>
                                        </div>
                                        <button
                                            type="button"
                                            data-testid={`neo-toggle-${location.id}`}
                                            data-insight-id={idx === 0 ? "locations.workflow.neo-toggle" : undefined}
                                            data-insight-label={idx === 0 ? "Per-location NEO toggle" : undefined}
                                            onClick={async () => {
                                                const next = !location.neo_enabled;
                                                try {
                                                    const API = window.location.origin;
                                                    const token = localStorage.getItem('yulontap_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
                                                    const res = await fetch(`${API}/api/neo/locations/${location.id}/toggle`, {
                                                        method: 'PUT',
                                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                                        body: JSON.stringify({ neo_enabled: next }),
                                                    });
                                                    if (!res.ok) throw new Error('toggle failed');
                                                    // Optimistic local update
                                                    location.neo_enabled = next;
                                                    toast.success(next ? 'NEO enabled' : 'NEO disabled');
                                                    loadData();
                                                } catch (e) {
                                                    toast.error('Failed to toggle NEO');
                                                }
                                            }}
                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2 ${location.neo_enabled ? 'bg-fuchsia-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                                            aria-pressed={!!location.neo_enabled}
                                            aria-label="Toggle NEO"
                                        >
                                            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${location.neo_enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                                        </button>
                                    </div>
                                    {location.neo_enabled && (
                                        neoMasterCards[location.id] ? (
                                            // Already has a NEO card — show URL + Copy
                                            (() => {
                                                const neoCard = neoMasterCards[location.id];
                                                const neoUrl = `${window.location.origin}/landing/${neoCard.card_id}`;
                                                return (
                                                    <div
                                                        className="flex flex-col gap-1.5 p-2.5 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5"
                                                        data-testid={`neo-url-panel-${location.id}`}
                                                        data-insight-id={idx === 0 ? "locations.workflow.neo-url-panel" : undefined}
                                                        data-insight-label={idx === 0 ? "NEO Manager URL panel" : undefined}
                                                    >
                                                        <div className="flex items-center justify-between gap-2">
                                                            <p className="text-[11px] uppercase tracking-wide text-fuchsia-600 dark:text-fuchsia-300 font-semibold">
                                                                NEO Manager URL
                                                            </p>
                                                            <span className="text-[10px] font-mono text-fuchsia-700 dark:text-fuchsia-300 bg-fuchsia-500/10 px-1.5 py-0.5 rounded">
                                                                {neoCard.card_id}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-slate-600 dark:text-slate-400 font-mono truncate" title={neoUrl}>
                                                            {neoUrl}
                                                        </p>
                                                        <div className="flex gap-1.5">
                                                            <button
                                                                type="button"
                                                                data-testid={`neo-copy-${location.id}`}
                                                                onClick={async () => {
                                                                    try {
                                                                        await navigator.clipboard.writeText(neoUrl);
                                                                        toast.success('URL copied to clipboard');
                                                                    } catch {
                                                                        // Clipboard API unavailable (Safari + non-HTTPS etc) — fallback
                                                                        const ta = document.createElement('textarea');
                                                                        ta.value = neoUrl;
                                                                        document.body.appendChild(ta);
                                                                        ta.select();
                                                                        try { document.execCommand('copy'); toast.success('URL copied'); }
                                                                        catch { toast.error('Copy failed — long-press to copy'); }
                                                                        document.body.removeChild(ta);
                                                                    }
                                                                }}
                                                                className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-fuchsia-600 text-white hover:bg-fuchsia-700 transition-colors"
                                                            >
                                                                <Copy className="w-3.5 h-3.5" />
                                                                Copy URL
                                                            </button>
                                                            <a
                                                                href={neoUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-testid={`neo-open-${location.id}`}
                                                                className="flex items-center justify-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-fuchsia-500/40 text-fuchsia-600 dark:text-fuchsia-300 hover:bg-fuchsia-500/10 transition-colors"
                                                            >
                                                                <ExternalLink className="w-3.5 h-3.5" />
                                                            </a>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            // No NEO card yet — show Create button (opens dialog)
                                            <button
                                                type="button"
                                                data-testid={`neo-provision-${location.id}`}
                                                data-insight-id={idx === 0 ? "locations.workflow.neo-provision" : undefined}
                                                data-insight-label={idx === 0 ? "Create NEO Master Card" : undefined}
                                                onClick={() => {
                                                    const suggested = (location.name || 'LOC').replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase() || 'LOC';
                                                    setNeoDialog({
                                                        open: true,
                                                        locationId: location.id,
                                                        locationName: location.name || '',
                                                        code: suggested,
                                                        submitting: false,
                                                    });
                                                }}
                                                className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-fuchsia-600 text-white hover:bg-fuchsia-700 transition-colors"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                                Create NEO Card
                                            </button>
                                        )
                                    )}
                                    </div>
                                )}

                                <div className="pt-3 border-t border-slate-100 dark:border-slate-700 text-sm text-muted-foreground">
                                    {t('common.created')} {parseUtc(location.created_at).toLocaleDateString()}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="py-16">
                        <EmptyState
                            icon={MapPin}
                            title={searchTerm ? t('empty.locationsFilterTitle', 'No locations found') : t('empty.locationsTitle', 'No locations yet')}
                            description={
                                searchTerm 
                                    ? t('empty.locationsFilterDesc', 'Try adjusting your search terms.')
                                    : t('empty.locationsDesc', 'Locations are physical places where your employees use Tap Engine Cards. Add your first location to start tracking performance.')
                            }
                            action={
                                canManageLocations() && !searchTerm && (
                                    <Button onClick={() => openModal()} data-testid="empty-add-location-btn" hidden={isPartner()}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        {t('empty.locationsAction', 'Add Your First Location')}
                                    </Button>
                                )
                            }
                        />
                    </CardContent>
                </Card>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {editingLocation ? t('locations.editLocation') : t('locations.addNewLocation')}
                        </DialogTitle>
                        <DialogDescription>
                            {editingLocation 
                                ? t('locations.editLocationDesc')
                                : t('locations.addLocationDesc')}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                        {/* Basic Info Section */}
                        <div className="space-y-4">
                            <div>
                                <Label className="mb-2 block font-medium">
                                    {t('locations.locationName')} <span className="text-red-500">*</span>
                                </Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., Downtown Branch"
                                    data-testid="location-name-input"
                                />
                            </div>
                            <div>
                                <Label className="mb-2 block font-medium">{t('locations.address')}</Label>
                                <Input
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    placeholder={t('locations.addressPlaceholder')}
                                    data-testid="location-address-input"
                                />
                            </div>
                            {isSuperAdmin() && organizations.length > 0 && (
                                <div>
                                    <Label className="mb-2 block font-medium">
                                        {t('clients.organization')} <span className="text-red-500">*</span>
                                    </Label>
                                    <Select 
                                        value={formData.organization_id} 
                                        onValueChange={(val) => setFormData({ ...formData, organization_id: val })}
                                    >
                                        <SelectTrigger data-testid="location-org-select">
                                            <SelectValue placeholder={t('locations.selectOrg')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {organizations.map(org => (
                                                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            {/* Auto-clear open recovery alerts. Archives on a
                                schedule; nothing is deleted and resolved items
                                are never touched. Only on an existing location. */}
                            {editingLocation && (
                                <div data-testid="location-auto-clear-row">
                                    <Label className="mb-2 block font-medium">
                                        {t('locations.autoClear.label')}
                                    </Label>
                                    <Select
                                        value={String(autoClearDays)}
                                        onValueChange={(val) => saveAutoClear(Number(val))}
                                    >
                                        <SelectTrigger data-testid="location-auto-clear-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="0" data-testid="location-auto-clear-0">{t('locations.autoClear.off')}</SelectItem>
                                            {[2, 7, 14, 30].map((d) => (
                                                <SelectItem key={d} value={String(d)} data-testid={`location-auto-clear-${d}`}>
                                                    {t('locations.autoClear.days', { count: d })}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="mt-1.5 text-xs text-muted-foreground">{t('locations.autoClear.help')}</p>
                                </div>
                            )}
                        </div>

                        {/* Baseline Values Section - Collapsible */}
                        {isSuperAdmin() && (
                            <Collapsible open={showBaseline} onOpenChange={setShowBaseline}>
                                <CollapsibleTrigger asChild>
                                    <Button 
                                        variant="outline" 
                                        className="w-full justify-between"
                                        data-testid="baseline-toggle-btn"
                                        data-insight-id="locations.workflow.baseline-values"
                                        data-insight-label="Starting values for ROI"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Star className="w-4 h-4 text-amber-500" />
                                            <span>{t('locations.startingValuesROI')}</span>
                                            <HelpTooltip text="Enter the current review counts and follower numbers for this location. This helps us calculate exactly how much value YulOnTap generates (new reviews since these starting values)." />
                                        </div>
                                        {showBaseline ? (
                                            <ChevronUp className="w-4 h-4" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4" />
                                        )}
                                    </Button>
                                </CollapsibleTrigger>
                                
                                <CollapsibleContent className="mt-4">
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 space-y-6">
                                        {/* Baseline Date */}
                                        <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                                            <Calendar className="w-5 h-5 text-muted-foreground" />
                                            <div className="flex-1">
                                                <Label className="mb-1 block text-sm font-medium">
                                                    Baseline Date
                                                    <HelpTooltip text="The date when you captured these starting values. Usually today for new locations." />
                                                </Label>
                                                <Input
                                                    type="date"
                                                    value={formData.baseline_values.baseline_date}
                                                    onChange={(e) => updateBaselineField('baseline_date', e.target.value)}
                                                    className="w-48"
                                                    data-testid="baseline-date-input"
                                                />
                                            </div>
                                        </div>

                                        {/* Google Section */}
                                        <div className={`p-4 rounded-lg border ${PLATFORM_CONFIG.google.color}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className={`w-8 h-8 rounded-lg ${PLATFORM_CONFIG.google.iconBg} flex items-center justify-center`}>
                                                    <span className="text-lg">G</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold">Google</span>
                                                    <p className="text-xs text-muted-foreground">{PLATFORM_CONFIG.google.description}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label className="mb-1 block text-sm">
                                                        Reviews
                                                        <HelpTooltip text="How many Google reviews does this location have right now? Check Google Business Profile." />
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 257"
                                                        value={formData.baseline_values.google_reviews}
                                                        onChange={(e) => updateBaselineField('google_reviews', e.target.value)}
                                                        data-testid="baseline-google-reviews"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="mb-1 block text-sm">
                                                        Rating (1-5)
                                                        <HelpTooltip text="What's the current star rating? e.g., 4.3" />
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        max="5"
                                                        step="0.1"
                                                        placeholder="e.g., 4.3"
                                                        value={formData.baseline_values.google_rating}
                                                        onChange={(e) => updateBaselineField('google_rating', e.target.value)}
                                                        data-testid="baseline-google-rating"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* P_PHASE_4_8_REPUTATION_BASELINE_FALLBACK
                                            — Current Snapshot (super_admin only,
                                            edit-mode only).
                                            Baseline above is the immutable historical
                                            anchor. Current Snapshot is the manually-
                                            maintained "today" number that prevents
                                            "— / 0 reviews generated" optics on the
                                            Weekly Report and Morning Brief. */}
                                        {isSuperAdmin() && editingLocation && (
                                            <div className="p-4 rounded-lg border border-amber-300 bg-amber-50/40 dark:bg-amber-950/20" data-testid="current-snapshot-panel">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div>
                                                        <span className="font-semibold">Current Snapshot</span>
                                                        <p className="text-xs text-muted-foreground">
                                                            Manually-maintained &quot;today&quot; reputation. Baseline above stays immutable.
                                                        </p>
                                                    </div>
                                                    {currentSnapshot.snapshot_updated_at && (
                                                        <span className="text-xs text-muted-foreground" data-testid="current-snapshot-updated-at">
                                                            Last updated {new Date(currentSnapshot.snapshot_updated_at).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <Label className="mb-1 block text-sm">Current Reviews</Label>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            placeholder="e.g., 2887"
                                                            value={currentSnapshot.google_reviews}
                                                            onChange={(e) => setCurrentSnapshot(s => ({ ...s, google_reviews: e.target.value }))}
                                                            data-testid="current-snapshot-reviews"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="mb-1 block text-sm">Current Rating (1-5)</Label>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            max="5"
                                                            step="0.1"
                                                            placeholder="e.g., 4.3"
                                                            value={currentSnapshot.google_rating}
                                                            onChange={(e) => setCurrentSnapshot(s => ({ ...s, google_rating: e.target.value }))}
                                                            data-testid="current-snapshot-rating"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mt-3 flex justify-end">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        onClick={handleSaveCurrentSnapshot}
                                                        disabled={savingSnapshot}
                                                        data-testid="current-snapshot-save-btn"
                                                    >
                                                        {savingSnapshot ? 'Saving…' : 'Save Snapshot'}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Facebook Section */}
                                        <div className={`p-4 rounded-lg border ${PLATFORM_CONFIG.facebook.color}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className={`w-8 h-8 rounded-lg ${PLATFORM_CONFIG.facebook.iconBg} flex items-center justify-center`}>
                                                    <span className="text-lg">f</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold">Facebook</span>
                                                    <p className="text-xs text-muted-foreground">{PLATFORM_CONFIG.facebook.description}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="mb-1 block text-sm">
                                                    Page Likes
                                                    <HelpTooltip text="How many people have liked your Facebook Business Page? Find this on your Page's About section." />
                                                </Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    placeholder="e.g., 1245"
                                                    value={formData.baseline_values.facebook_likes}
                                                    onChange={(e) => updateBaselineField('facebook_likes', e.target.value)}
                                                    data-testid="baseline-facebook-likes"
                                                />
                                            </div>
                                        </div>

                                        {/* Instagram Section */}
                                        <div className={`p-4 rounded-lg border ${PLATFORM_CONFIG.instagram.color}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className={`w-8 h-8 rounded-lg ${PLATFORM_CONFIG.instagram.iconBg} flex items-center justify-center`}>
                                                    <span className="text-lg">IG</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold">Instagram</span>
                                                    <p className="text-xs text-muted-foreground">{PLATFORM_CONFIG.instagram.description}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="mb-1 block text-sm">
                                                    Followers
                                                    <HelpTooltip text="How many followers does your Instagram business account have?" />
                                                </Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    placeholder="e.g., 2567"
                                                    value={formData.baseline_values.instagram_followers}
                                                    onChange={(e) => updateBaselineField('instagram_followers', e.target.value)}
                                                    data-testid="baseline-instagram-followers"
                                                />
                                            </div>
                                        </div>

                                        {/* Yelp Section */}
                                        <div className={`p-4 rounded-lg border ${PLATFORM_CONFIG.yelp.color}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className={`w-8 h-8 rounded-lg ${PLATFORM_CONFIG.yelp.iconBg} flex items-center justify-center`}>
                                                    <span className="text-lg">Y</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold">Yelp</span>
                                                    <p className="text-xs text-muted-foreground">{PLATFORM_CONFIG.yelp.description}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label className="mb-1 block text-sm">
                                                        Reviews
                                                        <HelpTooltip text="How many Yelp reviews does this location have?" />
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 134"
                                                        value={formData.baseline_values.yelp_reviews}
                                                        onChange={(e) => updateBaselineField('yelp_reviews', e.target.value)}
                                                        data-testid="baseline-yelp-reviews"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="mb-1 block text-sm">
                                                        Rating (1-5)
                                                        <HelpTooltip text="What's the current Yelp star rating? e.g., 4.1" />
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        max="5"
                                                        step="0.1"
                                                        placeholder="e.g., 4.1"
                                                        value={formData.baseline_values.yelp_rating}
                                                        onChange={(e) => updateBaselineField('yelp_rating', e.target.value)}
                                                        data-testid="baseline-yelp-rating"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Trustpilot Section */}
                                        <div className={`p-4 rounded-lg border ${PLATFORM_CONFIG.trustpilot.color}`}>
                                            <div className="flex items-center gap-2 mb-3">
                                                <div className={`w-8 h-8 rounded-lg ${PLATFORM_CONFIG.trustpilot.iconBg} flex items-center justify-center`}>
                                                    <span className="text-lg">TP</span>
                                                </div>
                                                <div>
                                                    <span className="font-semibold">Trustpilot</span>
                                                    <p className="text-xs text-muted-foreground">{PLATFORM_CONFIG.trustpilot.description}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label className="mb-1 block text-sm">
                                                        Reviews
                                                        <HelpTooltip text="How many Trustpilot reviews does this business have?" />
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 23"
                                                        value={formData.baseline_values.trustpilot_reviews}
                                                        onChange={(e) => updateBaselineField('trustpilot_reviews', e.target.value)}
                                                        data-testid="baseline-trustpilot-reviews"
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="mb-1 block text-sm">
                                                        Rating (1-5)
                                                        <HelpTooltip text="What's the current Trustpilot star rating? e.g., 4.5" />
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="1"
                                                        max="5"
                                                        step="0.1"
                                                        placeholder="e.g., 4.5"
                                                        value={formData.baseline_values.trustpilot_rating}
                                                        onChange={(e) => updateBaselineField('trustpilot_rating', e.target.value)}
                                                        data-testid="baseline-trustpilot-rating"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Helper Note */}
                                        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm text-blue-700">
                                                <p className="font-medium mb-1">All fields are optional!</p>
                                                <p>Only fill in the platforms this location uses. Leave others blank. 
                                                These starting values help us show exactly how many new reviews YulOnTap generated.</p>
                                            </div>
                                        </div>
                                    </div>
                                </CollapsibleContent>
                            </Collapsible>
                        )}
                    </div>

                    {/* v4.31.0 — Guest Routing & Review Platforms (per-location URL overrides).
                        Only shown when editing an existing location — needs a saved locationId. */}
                    {isEditingLocation && (
                        <LocationRoutingSection locationId={editingLocation.id} />
                    )}

                    <DialogFooter>
                        {isEditingLocation && (
                            <span
                                data-testid="location-dirty-indicator"
                                className={`text-xs font-medium ${isLocationDirty ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}
                                style={{ marginRight: 'auto' }}
                            >
                                {isLocationDirty
                                    ? t('locations.pendingChanges')
                                    : t('locations.allSettingsSaved')}
                            </span>
                        )}
                        <Button variant="outline" onClick={() => setIsModalOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} disabled={saving} data-testid="save-location-btn">
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {editingLocation ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Quick Set Baselines Modal */}
            <QuickSetBaselinesModal 
                open={quickSetOpen} 
                onOpenChange={setQuickSetOpen}
                onSuccess={loadData}
            />

            {/* CSV Import Modal */}
            <CSVBaselineImport
                open={csvImportOpen}
                onOpenChange={setCsvImportOpen}
                onSuccess={loadData}
            />

            {/* NEO Card provision — Dialog (replaces window.prompt) */}
            <Dialog
                open={neoDialog.open}
                onOpenChange={(o) => !neoDialog.submitting && setNeoDialog(s => ({ ...s, open: o }))}
            >
                <DialogContent data-testid="neo-provision-dialog" data-insight-id="locations.workflow.neo-create-dialog" data-insight-label="Create NEO Manager Card dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-md bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center">
                                <span className="text-white font-black text-[9px]">NEO</span>
                            </div>
                            Create NEO Manager Card
                        </DialogTitle>
                        <DialogDescription>
                            Generates a dedicated Master Manager card for <span className="font-semibold">{neoDialog.locationName}</span>. Scanning it bypasses the customer landing and opens the NEO login directly.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">3-letter location code</label>
                            <Input
                                data-testid="neo-dialog-code-input"
                                value={neoDialog.code}
                                onChange={(e) => setNeoDialog(s => ({ ...s, code: e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 6).toUpperCase() }))}
                                placeholder="DEL"
                                maxLength={6}
                                className="font-mono uppercase"
                                disabled={neoDialog.submitting}
                            />
                            <p className="text-[11px] text-muted-foreground">
                                Card ID will be: <span className="font-mono font-semibold">{(neoDialog.code || 'LOC') + 'NEO'}</span>
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setNeoDialog(s => ({ ...s, open: false }))}
                            disabled={neoDialog.submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="neo-dialog-submit"
                            disabled={neoDialog.submitting || !neoDialog.code}
                            onClick={async () => {
                                setNeoDialog(s => ({ ...s, submitting: true }));
                                const API = window.location.origin;
                                const token = localStorage.getItem('yulontap_token');
                                try {
                                    const res = await fetch(`${API}/api/neo/locations/${neoDialog.locationId}/provision-master-card`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ code: neoDialog.code }),
                                    });
                                    let data = {};
                                    try { data = await res.json(); } catch { /* non-JSON response */ }
                                    if (!res.ok) {
                                        const msg = stringifyDetail(data.detail) || `Failed (HTTP ${res.status})`;
                                        toast.error(msg);
                                        setNeoDialog(s => ({ ...s, submitting: false }));
                                        return;
                                    }
                                    const url = `${window.location.origin}/landing/${data.card_id}`;
                                    toast.success(`NEO card ${data.card_id} created`);
                                    setNeoDialog({ open: false, locationId: null, locationName: '', code: '', submitting: false });
                                    setNeoCreated({ open: true, cardId: data.card_id, url, copied: false });
                                    loadData();
                                } catch (e) {
                                    toast.error('Network error — please retry');
                                    setNeoDialog(s => ({ ...s, submitting: false }));
                                }
                            }}
                            className="bg-fuchsia-600 hover:bg-fuchsia-700"
                        >
                            {neoDialog.submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                            Create Card
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* NEO Card created — success panel with copyable URL */}
            <Dialog
                open={neoCreated.open}
                onOpenChange={(o) => setNeoCreated(s => ({ ...s, open: o }))}
            >
                <DialogContent data-testid="neo-created-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                            <Check className="w-5 h-5" />
                            NEO Card Created
                        </DialogTitle>
                        <DialogDescription>
                            Share this URL with the manager, or use it to configure the physical Tap card.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <p className="text-[11px] uppercase tracking-wide text-fuchsia-600 dark:text-fuchsia-300 font-semibold">{t('locations.cardId')}</p>
                            <p className="font-mono font-bold text-lg" data-testid="neo-created-card-id">{neoCreated.cardId}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-[11px] uppercase tracking-wide text-fuchsia-600 dark:text-fuchsia-300 font-semibold">{t('locations.managerEntryUrl')}</p>
                            <div className="flex items-stretch gap-1.5">
                                <input
                                    readOnly
                                    value={neoCreated.url}
                                    onClick={(e) => e.target.select()}
                                    data-testid="neo-created-url"
                                    className="flex-1 min-w-0 font-mono text-xs px-2.5 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300"
                                />
                                <Button
                                    data-testid="neo-created-copy"
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(neoCreated.url);
                                            setNeoCreated(s => ({ ...s, copied: true }));
                                            toast.success('URL copied');
                                            setTimeout(() => setNeoCreated(s => ({ ...s, copied: false })), 2000);
                                        } catch {
                                            const ta = document.createElement('textarea');
                                            ta.value = neoCreated.url;
                                            document.body.appendChild(ta);
                                            ta.select();
                                            try { document.execCommand('copy'); toast.success('URL copied'); }
                                            catch { toast.error('Copy failed'); }
                                            document.body.removeChild(ta);
                                        }
                                    }}
                                    className="bg-fuchsia-600 hover:bg-fuchsia-700 shrink-0"
                                >
                                    {neoCreated.copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setNeoCreated(s => ({ ...s, open: false }))}>
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Locations;
